import { requireOrdinaryEntityType } from '@einhaus/project314-shared';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import AdminLayout from '@/components/Layout/AdminLayout';
import { Button, Card, ConfirmationDialog, FormCheckbox, FormInput, MultiSelect } from '@components/UI';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { trpc } from '@/api/trpc';
import { useQueryClient } from '@tanstack/react-query';
import { useAppQuery } from '@/hooks/useAppQuery';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { showToast } from '@/components/Layout/Toast';
import type { EntityTypeUnion } from '@einhaus/project314-types';
import type { ScreenerRuleTree } from '@/types/appRouter';
import type { EditorGroupNode, ScreenerGroupInput } from './types/screenerEditor';
import {
    createEditorCondition,
    createEditorGroup,
    editorGroupToInput,
    entityTypeOptions,
    extractAlwaysIncludeSymbols,
    groupNodeToEditor,
    normalizeSymbols
} from './utils/screenerEditorUtils';
import RuleBuilder from './components/RuleBuilder';
import PreviewSection from './components/PreviewSection';
import PerformanceSection from './components/PerformanceSection';
import RunScreenSection from './components/RunScreenSection';
import RevisionHistory from './components/RevisionHistory';
import ExperimentsPanel from './components/ExperimentsPanel';
import { createScreenerExperimentId, useScreenerExperiments, type ScreenerExperiment } from './hooks/useScreenerExperiments';

function deepClone<T>(value: T): T {
    if (typeof structuredClone === 'function') return structuredClone(value);
    return JSON.parse(JSON.stringify(value)) as T;
}

const ScreenerEditor = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { id } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    const copyFromId = searchParams.get('copyFrom');

    const optimizerJobIdFromQuery = useMemo(() => {
        const raw = searchParams.get('optimizerJobId');
        if (!raw) return null;
        const parsed = Number(raw);
        return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
    }, [searchParams]);

    const isEditMode = id !== undefined && id !== 'new';
    const isCopyMode = !isEditMode && copyFromId !== null;
    const screenerRuleId = isEditMode ? Number(id) : null;
    const sourceId = isCopyMode ? Number(copyFromId) : isEditMode ? screenerRuleId : null;

    // Form state
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [entityTypes, setEntityTypes] = useState<EntityTypeUnion['entityType'][]>(['Asset']);
    const [rootGroup, setRootGroup] = useState<EditorGroupNode>(() => createEditorGroup('and', [createEditorCondition()]));
    const [alwaysIncludeSymbols, setAlwaysIncludeSymbols] = useState('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [formLoaded, setFormLoaded] = useState(false);
    const [isBuilderCollapsed, setIsBuilderCollapsed] = useState(false);
    const [activeRightTab, setActiveRightTab] = useState('performance');

    const [draftLoadedFrom, setDraftLoadedFrom] = useState<
        null | { kind: 'revision'; id: number; label: string } | { kind: 'experiment'; id: string; label: string }
    >(null);

    // Queries
    const screenerQuery = useAppQuery(
        {
            ...trpc.screener.get.queryOptions({ screenerRuleId: sourceId ?? 0 }),
            enabled: sourceId !== null && sourceId > 0
        },
        { fallbackErrorMessage: 'Failed to load screener', showErrorToast: true }
    );

    const featuresQuery = useAppQuery(
        {
            ...trpc.screener.availableFeatures.queryOptions()
        },
        { fallbackErrorMessage: 'Failed to load features', showErrorToast: true }
    );

    const historyQuery = useAppQuery(
        {
            ...trpc.screener.history.queryOptions({ screenerRuleId: screenerRuleId ?? 0 }),
            enabled: isEditMode && screenerRuleId !== null
        },
        { fallbackErrorMessage: 'Failed to load revision history', showErrorToast: true }
    );

    useEffect(() => {
        setFormLoaded(false);
        setDraftLoadedFrom(null);

        if (sourceId === null) {
            setName('');
            setDescription('');
            setIsActive(true);
            setEntityTypes(['Asset']);
            setRootGroup(createEditorGroup('and', [createEditorCondition()]));
            setAlwaysIncludeSymbols('');
        }
    }, [sourceId]);

    const features = useMemo(() => featuresQuery.data?.features ?? [], [featuresQuery.data?.features]);

    const rootGroupInputResult = useMemo((): { ok: true; value: ScreenerGroupInput } | { ok: false; error: string } => {
        try {
            let rootGroupInput = editorGroupToInput(rootGroup);
            const includeSymbols = normalizeSymbols(alwaysIncludeSymbols);

            if (includeSymbols.length > 0) {
                rootGroupInput = {
                    type: 'group',
                    operator: 'or',
                    children: [
                        rootGroupInput,
                        {
                            type: 'condition',
                            featureKey: 'entity.symbol',
                            operator: 'any',
                            valueType: 'stringList',
                            values: includeSymbols,
                            missingPolicy: 'failClosed'
                        }
                    ]
                };
            }

            return { ok: true, value: rootGroupInput };
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Invalid rule input';
            return { ok: false, error: message };
        }
    }, [alwaysIncludeSymbols, rootGroup]);

    const screenerRootId = useMemo(() => {
        const rule = screenerQuery.data?.screener?.rule;
        if (!rule) return null;
        return rule.rootRuleId ?? rule.id;
    }, [screenerQuery.data]);

    const { experiments, addExperiment, updateExperiment, deleteExperiment, clearExperiments } = useScreenerExperiments(screenerRootId);

    // Populate form from fetched data
    useEffect(() => {
        const data = screenerQuery.data?.screener;
        if (!data || formLoaded) return;
        if (sourceId === null) return;
        if (data.rule.id !== sourceId) return;

        setName(isCopyMode ? `${data.rule.name} (Copy)` : data.rule.name);
        setDescription(data.rule.description ?? '');
        setIsActive(data.rule.isActive === 1);
        setEntityTypes(data.entityTypes.map(requireOrdinaryEntityType));

        const extracted = extractAlwaysIncludeSymbols(data.rootGroup);

        if (extracted) {
            setAlwaysIncludeSymbols(extracted.symbols.join(', '));
            setRootGroup(groupNodeToEditor(extracted.strippedRootGroup));
        } else {
            setAlwaysIncludeSymbols('');
            setRootGroup(groupNodeToEditor(data.rootGroup));
        }

        setFormLoaded(true);
    }, [formLoaded, isCopyMode, screenerQuery.data, sourceId]);

    // Mutations
    const createMutation = useTrpcMutation(trpc.screener.create.mutationOptions({}), {
        successMessage: 'Screener created',
        fallbackErrorMessage: 'Failed to create screener'
    });

    const reviseMutation = useTrpcMutation(trpc.screener.revise.mutationOptions({}), {
        successMessage: 'Screener revised',
        fallbackErrorMessage: 'Failed to revise screener'
    });

    const deleteMutation = useTrpcMutation(trpc.screener.delete.mutationOptions({}), {
        successMessage: 'Screener deleted',
        fallbackErrorMessage: 'Failed to delete screener'
    });

    const isSaving = createMutation.isPending || reviseMutation.isPending;

    const handleSave = async () => {
        if (!name.trim()) {
            showToast('Name is required', 'error');
            return;
        }

        if (!entityTypes.length) {
            showToast('Select at least one entity type', 'error');
            return;
        }

        try {
            if (!rootGroupInputResult.ok) {
                showToast(rootGroupInputResult.error, 'error');
                return;
            }

            const rootGroupInput = rootGroupInputResult.value;

            if (!isEditMode) {
                // Create mode (including copy mode)
                const result = await createMutation.mutateAsync({
                    name: name.trim(),
                    description: description.trim().length ? description.trim() : null,
                    isActive,
                    entityTypes,
                    rootGroup: rootGroupInput
                });

                if (result.id) {
                    void navigate(`/tools/screener/${result.id}`, { replace: true });
                }
            } else {
                // Versioned save (immutable)
                const result = await reviseMutation.mutateAsync({
                    screenerRuleId: screenerRuleId!,
                    name: name.trim(),
                    description: description.trim().length ? description.trim() : null,
                    isActive,
                    entityTypes,
                    rootGroup: rootGroupInput
                });

                if (result.id) {
                    setDraftLoadedFrom(null);

                    void queryClient.invalidateQueries({
                        queryKey: trpc.screener.history.queryOptions({ screenerRuleId: screenerRuleId! }).queryKey
                    });

                    void queryClient.invalidateQueries({
                        queryKey: trpc.screener.history.queryOptions({ screenerRuleId: result.id }).queryKey
                    });

                    void queryClient.invalidateQueries({
                        queryKey: trpc.screener.get.queryOptions({ screenerRuleId: screenerRuleId! }).queryKey
                    });

                    void queryClient.invalidateQueries({
                        queryKey: trpc.screener.get.queryOptions({ screenerRuleId: result.id }).queryKey
                    });

                    void navigate(`/tools/screener/${result.id}`, { replace: true });
                }
            }
        } catch (error) {
            if (error instanceof Error && !error.message.includes('Failed to')) {
                showToast(error.message, 'error');
            }
        }
    };

    const handleDelete = async () => {
        if (!screenerRuleId) return;

        try {
            await deleteMutation.mutateAsync({ screenerRuleId });
            setShowDeleteConfirm(false);
            void navigate('/tools/screeners');
        } catch {
            // Toast handled by useTrpcMutation
        }
    };

    const applyScreenerToDraft = (screener: ScreenerRuleTree) => {
        setName(screener.rule.name);
        setDescription(screener.rule.description ?? '');
        setIsActive(screener.rule.isActive === 1);
        setEntityTypes(screener.entityTypes.map(requireOrdinaryEntityType));

        const extracted = extractAlwaysIncludeSymbols(screener.rootGroup);

        if (extracted) {
            setAlwaysIncludeSymbols(extracted.symbols.join(', '));
            setRootGroup(groupNodeToEditor(extracted.strippedRootGroup));
        } else {
            setAlwaysIncludeSymbols('');
            setRootGroup(groupNodeToEditor(screener.rootGroup));
        }
    };

    const handleLoadRevisionIntoDraft = async (revisionId: number) => {
        try {
            const data = await queryClient.fetchQuery(trpc.screener.get.queryOptions({ screenerRuleId: revisionId }));
            applyScreenerToDraft(data.screener);
            setDraftLoadedFrom({ kind: 'revision', id: revisionId, label: `revision #${revisionId}` });
            showToast(`Loaded revision #${revisionId} into draft`, 'success');
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to load revision';
            showToast(message, 'error');
        }
    };

    const handleLoadExperimentIntoDraft = (exp: ScreenerExperiment) => {
        setName(exp.config.name);
        setDescription(exp.config.description);
        setIsActive(exp.config.isActive);
        setEntityTypes(exp.config.entityTypes.map(requireOrdinaryEntityType));
        setAlwaysIncludeSymbols(exp.config.alwaysIncludeSymbols);
        setRootGroup(deepClone(exp.config.rootGroup));
        setDraftLoadedFrom({ kind: 'experiment', id: exp.id, label: exp.label });
        showToast(`Loaded experiment: ${exp.label}`, 'success');
    };

    const handleCaptureExperiment = (payload: {
        source: 'auto' | 'manual';
        jobId: number;
        rootGroupInput: ScreenerGroupInput;
        eval: { metrics: ScreenerExperiment['metrics']; ruleSummary: string };
        label: string;
        note: string | null;
    }) => {
        if (!isEditMode || screenerRuleId === null) {
            showToast('Save the screener first to capture experiments', 'error');
            return;
        }

        if (screenerRootId === null) {
            showToast('Screener data not loaded yet', 'error');
            return;
        }

        const createdAt = new Date().toISOString();
        const defaultLabel = `Run ${dayjs(createdAt).format('MMM D, h:mm A')}`;

        const latest = experiments[0] ?? null;

        const latestMatches =
            payload.source === 'manual' &&
            latest !== null &&
            latest.jobId === payload.jobId &&
            JSON.stringify(latest.config.rootGroupInput) === JSON.stringify(payload.rootGroupInput);

        const latestAgeMs = latestMatches ? Date.now() - new Date(latest.createdAt).getTime() : Number.POSITIVE_INFINITY;

        if (latestMatches && latestAgeMs >= 0 && latestAgeMs < 2 * 60 * 1000) {
            const nextLabel = payload.label.trim().length ? payload.label.trim() : latest.label;
            const nextNote = payload.note ?? latest.note;

            updateExperiment(latest.id, {
                label: nextLabel,
                note: nextNote,
                ruleSummary: payload.eval.ruleSummary,
                metrics: payload.eval.metrics,
                config: {
                    name,
                    description,
                    isActive,
                    entityTypes,
                    rootGroup: deepClone(rootGroup),
                    alwaysIncludeSymbols,
                    rootGroupInput: payload.rootGroupInput
                }
            });

            showToast('Experiment updated', 'success');
            return;
        }

        const exp: ScreenerExperiment = {
            id: createScreenerExperimentId(),
            createdAt,
            label: payload.label.trim().length ? payload.label.trim() : defaultLabel,
            note: payload.note,
            starred: false,
            jobId: payload.jobId,
            ruleSummary: payload.eval.ruleSummary,
            metrics: payload.eval.metrics,
            config: {
                name,
                description,
                isActive,
                entityTypes,
                rootGroup: deepClone(rootGroup),
                alwaysIncludeSymbols,
                rootGroupInput: payload.rootGroupInput
            },
            source: { screenerRuleId, screenerRootId }
        };

        addExperiment(exp);
        if (payload.source === 'manual') showToast('Experiment captured', 'success');
    };

    // Loading state
    if (sourceId !== null && screenerQuery.isLoading) {
        return (
            <AdminLayout title="Screener Editor">
                <DataLoadingSpinner text="Loading screener..." />
            </AdminLayout>
        );
    }

    const pageTitle = isEditMode ? `Edit: ${name || 'Screener'}` : isCopyMode ? 'Copy Screener' : 'New Screener';

    const rightTabs = [
        { id: 'performance', label: 'Performance', badge: experiments.length > 0 ? String(experiments.length) : null },
        { id: 'preview', label: 'Preview', badge: null },
        { id: 'run', label: 'Run Screen', badge: null },
        {
            id: 'history',
            label: 'History',
            badge: historyQuery.data?.revisions?.length ? String(historyQuery.data.revisions.length) : null
        }
    ];

    return (
        <AdminLayout containerSize="1920px" title="Screener Editor">
            <div className="flex flex-col" style={{ height: 'calc(100vh - 160px)' }}>
                {/* Header */}
                <div className="flex justify-between items-center mb-4 shrink-0">
                    <div>
                        <h3 className="text-white text-xl font-semibold">{pageTitle}</h3>
                        {isEditMode && screenerRuleId && <div className="text-sm text-zinc-400 mt-1">ID: {screenerRuleId}</div>}
                    </div>
                    <div className="flex gap-2">
                        <Button variant="secondary" onClick={() => navigate('/tools/screeners')}>
                            Cancel
                        </Button>
                        {isEditMode && (
                            <Button variant="danger" onClick={() => setShowDeleteConfirm(true)}>
                                Delete
                            </Button>
                        )}
                        <Button disabled={isSaving} onClick={handleSave}>
                            {isSaving ? 'Saving…' : isEditMode ? 'Save Version' : 'Save'}
                        </Button>
                    </div>
                </div>

                {draftLoadedFrom && (
                    <div className="mb-3 flex items-start justify-between gap-3 rounded-lg border border-blue-900/40 bg-blue-900/10 px-3 py-2 shrink-0">
                        <div className="text-xs text-blue-200">
                            Draft loaded from{' '}
                            {draftLoadedFrom.kind === 'revision'
                                ? `revision #${draftLoadedFrom.id}`
                                : `experiment "${draftLoadedFrom.label}"`}
                            . Save Version to commit.
                        </div>
                        <button
                            className="text-xs text-blue-200/80 hover:text-blue-100 px-2 py-1 rounded hover:bg-blue-900/20 transition-colors"
                            type="button"
                            onClick={() => setDraftLoadedFrom(null)}
                        >
                            Dismiss
                        </button>
                    </div>
                )}

                {/* Split panels */}
                <div className="flex flex-1 min-h-0 gap-4">
                    {/* Left panel: Builder */}
                    {!isBuilderCollapsed && (
                        <div className="w-[38%] shrink-0 overflow-y-auto border-r border-zinc-800 pr-4 space-y-4">
                            <Card>
                                <h4 className="text-white font-medium mb-3">Basic Information</h4>

                                <FormInput label="Name" value={name} onChange={(e) => setName(e.target.value)} />
                                <FormInput label="Description" value={description} onChange={(e) => setDescription(e.target.value)} />

                                <div className="grid grid-cols-2 gap-4">
                                    <FormCheckbox checked={isActive} label="Active" onChange={(e) => setIsActive(e.target.checked)} />
                                    <div className="text-xs text-zinc-500 flex items-end">Saves create a new version (recommended).</div>
                                </div>

                                <MultiSelect
                                    isMulti
                                    label="Entity Types"
                                    options={entityTypeOptions}
                                    value={entityTypes.map((t) => ({ value: t, label: t }))}
                                    onChange={(newValue) => {
                                        const values = Array.isArray(newValue)
                                            ? newValue.map((v) => v.value as EntityTypeUnion['entityType'])
                                            : [];
                                        setEntityTypes(values);
                                    }}
                                />

                                <div className="mt-2">
                                    <FormInput
                                        helpText="These symbols will qualify even if they fail other conditions (OR logic)."
                                        label="Always Include Symbols (optional)"
                                        placeholder="SPY, QQQ"
                                        value={alwaysIncludeSymbols}
                                        onChange={(e) => setAlwaysIncludeSymbols(e.target.value)}
                                    />
                                </div>
                            </Card>

                            <Card>
                                <h4 className="text-white font-medium mb-3">Rules</h4>
                                <RuleBuilder
                                    entityTypes={entityTypes}
                                    features={features}
                                    featuresLoading={featuresQuery.isLoading}
                                    rootGroup={rootGroup}
                                    onChange={setRootGroup}
                                />
                            </Card>
                        </div>
                    )}

                    {/* Right panel */}
                    <div className="flex-1 min-w-0 flex flex-col">
                        {/* Tab bar with collapse toggle */}
                        <div className="flex items-center gap-1 shrink-0 mb-4">
                            <button
                                className="flex items-center justify-center w-8 h-8 rounded-md border border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors mr-2"
                                title={isBuilderCollapsed ? 'Show builder panel' : 'Hide builder panel'}
                                type="button"
                                onClick={() => setIsBuilderCollapsed((prev) => !prev)}
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                    {isBuilderCollapsed ? (
                                        <path d="M13 5l7 7-7 7M5 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                                    ) : (
                                        <path d="M11 19l-7-7 7-7m8 14l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
                                    )}
                                </svg>
                            </button>

                            {isEditMode &&
                                rightTabs.map((tab) => (
                                    <button
                                        className={`px-4 py-2 rounded-md text-sm transition-colors duration-200 ${
                                            activeRightTab === tab.id ? 'bg-p314-blue text-white' : 'text-zinc-300 hover:bg-zinc-700'
                                        }`}
                                        key={tab.id}
                                        type="button"
                                        onClick={() => setActiveRightTab(tab.id)}
                                    >
                                        {tab.label}
                                        {tab.badge && <span className="ml-1.5 text-xs opacity-70">({tab.badge})</span>}
                                    </button>
                                ))}
                        </div>

                        {/* Tab content */}
                        <div className="flex-1 overflow-y-auto min-h-0">
                            {!isEditMode || screenerRuleId === null ? (
                                <div className="flex flex-col items-center justify-center h-full text-zinc-400">
                                    <div className="text-sm">
                                        Save the screener to access Performance, Preview, Run Screen, and History.
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {activeRightTab === 'performance' && (
                                        <div className="space-y-4">
                                            <Card>
                                                <PerformanceSection
                                                    initialJobId={optimizerJobIdFromQuery}
                                                    rootGroupInput={rootGroupInputResult.ok ? rootGroupInputResult.value : null}
                                                    rootGroupInputError={rootGroupInputResult.ok ? null : rootGroupInputResult.error}
                                                    screenerRuleId={screenerRuleId}
                                                    onCapture={screenerRootId !== null ? handleCaptureExperiment : undefined}
                                                />
                                            </Card>
                                            <Card>
                                                <ExperimentsPanel
                                                    experiments={experiments}
                                                    onClearExperiments={clearExperiments}
                                                    onDeleteExperiment={deleteExperiment}
                                                    onLoadExperiment={handleLoadExperimentIntoDraft}
                                                    onUpdateExperiment={updateExperiment}
                                                />
                                            </Card>
                                        </div>
                                    )}

                                    {activeRightTab === 'preview' && (
                                        <Card>
                                            <PreviewSection screenerRuleId={screenerRuleId} />
                                        </Card>
                                    )}

                                    {activeRightTab === 'run' && (
                                        <Card>
                                            <RunScreenSection screenerRuleId={screenerRuleId} />
                                        </Card>
                                    )}

                                    {activeRightTab === 'history' && (
                                        <Card>
                                            <div className="flex items-center justify-between gap-3 mb-3">
                                                <div>
                                                    <h4 className="text-white font-medium">Versions</h4>
                                                    <div className="text-xs text-zinc-500">
                                                        Load any previous version into the draft, then Save Version.
                                                    </div>
                                                </div>
                                            </div>

                                            {historyQuery.isLoading ? (
                                                <DataLoadingSpinner />
                                            ) : (
                                                <RevisionHistory
                                                    currentScreenerRuleId={screenerRuleId ?? 0}
                                                    revisions={historyQuery.data?.revisions ?? []}
                                                    onLoadRevision={handleLoadRevisionIntoDraft}
                                                />
                                            )}
                                        </Card>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Delete confirmation */}
            <ConfirmationDialog
                confirmText="Delete"
                confirmVariant="danger"
                isConfirmLoading={deleteMutation.isPending}
                isOpen={showDeleteConfirm}
                message={`Are you sure you want to delete "${name}"? This action cannot be undone.`}
                title="Delete Screener"
                onCancel={() => setShowDeleteConfirm(false)}
                onConfirm={handleDelete}
            />
        </AdminLayout>
    );
};

export default ScreenerEditor;
